import { useFormik } from 'formik'
import { useQuery, useQueryClient } from 'react-query'
import { applyApiFieldErrors, ButtonType, showError } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  Button,
  FormInput,
  FormToggle,
  LoadingIndicator,
  SectionCard,
  useToast,
} from 'app/components'
import { CampoParametro, ValoresParametro } from '../core/_models'
import { parametrosSchema } from './validation/parametrosSchema'

type Props<T extends ValoresParametro> = {
  id: string
  titulo: string
  descricao: string
  campos: CampoParametro<T>[]
  buscar: () => Promise<T>
  salvar: (dto: T) => Promise<unknown>
}

export const ParametrosCard = <T extends ValoresParametro>({
  id,
  titulo,
  descricao,
  campos,
  buscar,
  salvar,
}: Props<T>) => {
  const { t } = useT()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const chave = `parametros-${id}`
  const formId = `${chave}-form`
  const { data, isLoading, isError } = useQuery(chave, buscar, {
    refetchOnWindowFocus: false,
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: Object.fromEntries(
      campos.map((c) => [c.name, data?.[c.name] ?? (c.tipo === 'booleano' ? false : '')])
    ),
    validationSchema: parametrosSchema(campos, t),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const dto = Object.fromEntries(
          campos.map((c) => [
            c.name,
            c.tipo === 'booleano' ? Boolean(values[c.name]) : Number(values[c.name]),
          ])
        ) as T
        await salvar(dto)
        await queryClient.invalidateQueries(chave)
        showToast({ variant: 'success', title: t('parametros.salvo', { titulo }) })
      } catch (error) {
        const temOutroErro = applyApiFieldErrors(
          error,
          formik.setFieldError,
          campos.map((c) => c.name)
        )
        if (temOutroErro) showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const carregado = !isLoading && !isError

  return (
    <SectionCard
      title={titulo}
      description={descricao}
      footer={
        carregado && (
          <Button
            type={ButtonType.Submit}
            form={formId}
            className='btn-primary'
            disabled={formik.isSubmitting || !formik.dirty}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
          </Button>
        )
      }
    >
      {isLoading ? (
        <LoadingIndicator />
      ) : isError ? (
        <p className='text-danger mb-0' role='alert'>
          {t('parametros.erro_carregar')}
        </p>
      ) : (
        <form id={formId} onSubmit={formik.handleSubmit} noValidate className='row'>
          {campos.map((c) => (
            <div key={c.name} className={c.tipo === 'booleano' ? 'col-12' : 'col-md-6'}>
              {c.tipo === 'booleano' ? (
                <FormToggle
                  formik={formik}
                  name={c.name}
                  label={c.label}
                  infoText={c.infoText}
                  className='mb-3'
                  isDisabled={formik.isSubmitting}
                  align='start'
                />
              ) : (
                <FormInput
                  formik={formik}
                  name={c.name}
                  type='number'
                  label={c.label}
                  infoText={c.infoText}
                  className='mb-3'
                  isDisabled={formik.isSubmitting}
                  isRequired
                />
              )}
            </div>
          ))}
        </form>
      )}
    </SectionCard>
  )
}
